import React, { useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Receipt, Download, FileText, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const today = new Date();
const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

export default function AccountsGSTReturns() {
    const [period, setPeriod] = useState(defaultPeriod);
    const [busy, setBusy] = useState(null);

    const download = async (kind) => {
        setBusy(kind);
        try {
            const res = await api.get(`/accounts/gst-returns/${kind}.xlsx`, {
                params: { period }, responseType: "blob",
            });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(new Blob([res.data]));
            a.download = `${kind.toUpperCase()}-${period}.xlsx`;
            a.click();
            toast.success(`${kind.toUpperCase()} downloaded`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Download failed");
        } finally { setBusy(null); }
    };

    return (
        <div data-testid="accounts-gst-returns" className="max-w-3xl">
            <Toaster position="top-center" />
            <div className="mb-6">
                <div className="label-eyebrow text-sky-700 flex items-center gap-2"><Receipt className="w-3.5 h-3.5" />Tally-style</div>
                <h1 className="font-serif text-4xl text-slate-900 mt-2">GST Returns</h1>
                <p className="text-sm text-stone-600 mt-2">Download monthly GSTR-1 (outward supplies, B2B + B2C) and GSTR-3B (summary) as Excel files. Hand them over to your CA or upload to the GST portal.</p>
            </div>

            <div className="bg-white border border-sky-100 p-6 space-y-5">
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Return period</Label>
                    <Input
                        type="month"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="rounded-none border-stone-300 mt-1 max-w-xs"
                        data-testid="gst-period"
                    />
                    <p className="text-[11px] text-stone-500 mt-1">Pick any month. The file aggregates every non-cancelled invoice in that month.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <Card
                        title="GSTR-1"
                        subtitle="Outward supplies — invoice level"
                        body="One sheet for B2B (with GSTIN), one for B2C, plus a summary by GST rate."
                        onClick={() => download("gstr1")}
                        busy={busy === "gstr1"}
                        testId="dl-gstr1"
                    />
                    <Card
                        title="GSTR-3B"
                        subtitle="Monthly summary"
                        body="Single-sheet summary — taxable value, IGST, CGST, SGST, totals."
                        onClick={() => download("gstr3b")}
                        busy={busy === "gstr3b"}
                        testId="dl-gstr3b"
                    />
                </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 px-4 py-3 mt-4 text-xs text-amber-900">
                <strong>Heads up:</strong> these files are generated from the Sales Register data on this site. If you also raise invoices outside this site (e.g. directly in Tally), reconcile manually before filing.
            </div>
        </div>
    );
}

const Card = ({ title, subtitle, body, onClick, busy, testId }) => (
    <div className="border border-stone-200 p-4 hover:border-sky-700 transition-colors">
        <div className="flex items-center justify-between mb-2">
            <FileText className="w-5 h-5 text-sky-700" />
            <span className="text-[10px] uppercase tracking-widest text-stone-500">.xlsx</span>
        </div>
        <h3 className="font-serif text-xl text-slate-900">{title}</h3>
        <div className="text-[11px] uppercase tracking-widest text-stone-500 mt-0.5">{subtitle}</div>
        <p className="text-xs text-stone-600 mt-2 leading-relaxed">{body}</p>
        <Button onClick={onClick} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white w-full mt-4" data-testid={testId}>
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</> : <><Download className="w-4 h-4 mr-2" />Download</>}
        </Button>
    </div>
);
